/**
 * 键盘输入单例：动作映射 + 上升沿检测。
 * 每帧逻辑结束后调用 update() 刷新边沿状态。
 */

/** 动作 → KeyboardEvent.code 列表 */
const BINDINGS = {
  up: ['ArrowUp'],
  down: ['ArrowDown'],
  left: ['ArrowLeft'],
  right: ['ArrowRight'],
  shot: ['KeyZ'],
  bomb: ['KeyX'],
  special: ['KeyC'],
  slow: ['ShiftLeft', 'ShiftRight'],
  pause: ['KeyP', 'Escape'],
  confirm: ['KeyZ', 'Enter'],
  cancel: ['KeyX', 'Escape'],
  fps: ['KeyF'],
};

export const Input = {
  /** @type {Set<string>} 当前按下的动作 */
  down: new Set(),
  /** @type {Set<string>} 本帧刚按下的动作（上升沿） */
  justPressed: new Set(),
  _anyKey: false,
  _anyKeyEdge: false,

  /**
   * 绑定键盘事件。direction 键阻止默认滚动行为。
   * @param {Window|HTMLElement} target
   */
  init(target) {
    target.addEventListener('keydown', (e) => {
      const code = e.code;
      if (code.startsWith('Arrow')) e.preventDefault();
      this._anyKey = true;
      for (const action in BINDINGS) {
        if (BINDINGS[action].includes(code) && !this.down.has(action)) {
          this.down.add(action);
          this.justPressed.add(action);
        }
      }
    });
    target.addEventListener('keyup', (e) => {
      const code = e.code;
      // 先从原始集合移除本键，再判断动作是否仍有其他键按住
      // （顺序反了会导致 stillDown 永远为真、按键卡死）
      this._codesDown.delete(code);
      for (const action in BINDINGS) {
        const codes = BINDINGS[action];
        if (codes.includes(code)) {
          // 同一动作可能绑定多个键（如 ShiftLeft/Right），全部松开才算释放
          const stillDown = codes.some((c) => this._codesDown.has(c));
          if (!stillDown) this.down.delete(action);
        }
      }
    });
    // 用 keydown/keyup 维护原始按键集合，供多键绑定的动作判断
    target.addEventListener('keydown', (e) => this._codesDown.add(e.code));
    target.addEventListener('blur', () => this._reset());
  },

  /** @type {Set<string>} 原始按下的 KeyboardEvent.code */
  _codesDown: new Set(),

  /**
   * 动作当前是否按住。
   * @param {string} action
   * @returns {boolean}
   */
  isDown(action) {
    return this.down.has(action);
  },

  /**
   * 动作本帧是否刚按下（上升沿，update() 后失效）。
   * @param {string} action
   * @returns {boolean}
   */
  pressed(action) {
    return this.justPressed.has(action);
  },

  /**
   * 方向轴 {x, y}，取值 -1/0/1，斜向已归一化为长度 1。
   * @returns {{x: number, y: number}}
   */
  axis() {
    let x = 0;
    let y = 0;
    if (this.down.has('left')) x -= 1;
    if (this.down.has('right')) x += 1;
    if (this.down.has('up')) y -= 1;
    if (this.down.has('down')) y += 1;
    if (x !== 0 && y !== 0) {
      const inv = Math.SQRT1_2;
      x *= inv;
      y *= inv;
    }
    return { x, y };
  },

  /** 每帧末尾调用：清除边沿状态。 */
  update() {
    this.justPressed.clear();
    this._anyKeyEdge = this._anyKey;
    this._anyKey = false;
  },

  /**
   * 本帧是否有任意键按下（用于"按任意键继续"）。
   * @returns {boolean}
   */
  anyKeyPressed() {
    return this._anyKeyEdge;
  },

  /** 失焦时清空全部状态，防止按键卡死。 */
  _reset() {
    this.down.clear();
    this.justPressed.clear();
    this._codesDown.clear();
    this._anyKey = false;
    this._anyKeyEdge = false;
  },
};
